import { useEffect, useRef } from "react";
import { stepDependenciesQueryOptions } from "~/components/build/Show/lib/queries/stepDependencies";
import { useQueryClient } from "@tanstack/react-query";
import { Build, Step, StepDependencyContext } from "~/components/build/Show/lib/types";
import { useStepDependenciesStore } from "~/components/build/Show/lib/useStepDependenciesStore";

type UseStepDependenciesReturn = {
  dependencies: StepDependencyContext["dependencies"];
  missingDependencies: StepDependencyContext["missing_dependencies"];
};

export function useStepDependencies(
  stepUuid: Step["uuid"] | null | undefined,
): UseStepDependenciesReturn {
  if (!stepUuid) {
    return {
      dependencies: [],
      missingDependencies: [],
    };
  }

  const dependenciesByStep = useStepDependenciesStore((state) => state.dependenciesByStep);
  const { dependencies = [], missing_dependencies: missingDependencies = [] } =
    dependenciesByStep[stepUuid] || {};

  return {
    dependencies,
    missingDependencies,
  };
}

/**
 * Ensure the step dependency graph is fetched and stored in the useStepDependenciesStore.
 * Sets a data attribute on the body element for test environments.
 */
export function useEnsureStepDependencies(build: Build) {
  const queryClient = useQueryClient();
  const stepDependenciesLoaded = useRef(false);

  useEffect(() => {
    queryClient
      .ensureQueryData(stepDependenciesQueryOptions(build, build.stepUploadsCount))
      .then(() => {
        if (!stepDependenciesLoaded.current) {
          stepDependenciesLoaded.current = true;
          document.body.setAttribute("data-step-dependencies-loaded", "true");
        }
      });
  }, [build.id, build.stepUploadsCount, queryClient]);
}
